import React, { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import type { LinkProps } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Icon } from 'apps/promo/components/common/Icon/Icon';

import { CommonButton, IconContainer, LinkButton, ReactLinkButton, type ButtonSize, type ButtonVariant } from './styles';

type CommonButtonProps = ComponentPropsWithoutRef<typeof CommonButton>;
type NativeButtonType = NonNullable<ComponentPropsWithoutRef<'button'>['type']>;

type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

export interface ButtonProps extends Omit<CommonButtonProps, 'type'> {
    type?: NativeButtonType | 'link' | 'reactLink';
    caption?: ReactNode;
    iconName?: string;
    dataTestId?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    href?: string;
    rel?: string;
    target?: string;
    to?: LinkProps['to'];
}

export const Button = forwardRef<ButtonElement, ButtonProps>(({ type, dataTestId, caption, iconName, children, to, ...rest }, ref) => {
    if (type === 'link') {
        const linkButtonProps = rest as ComponentPropsWithoutRef<typeof LinkButton>;
        return (
            <LinkButton ref={ref as Ref<HTMLAnchorElement>} data-testid={dataTestId} {...linkButtonProps}>
                {caption}
                {iconName && (
                    <IconContainer>
                        <Icon name={iconName} size="24px" leftIndent="8px" />
                    </IconContainer>
                )}
            </LinkButton>
        );
    }
    if (type === 'reactLink') {
        const linkTo = to ?? '#';
        const routerLinkProps = rest as Omit<ComponentPropsWithoutRef<typeof ReactLinkButton>, 'to'>;
        return (
            <ReactLinkButton ref={ref as Ref<HTMLAnchorElement>} data-testid={dataTestId} to={linkTo} {...routerLinkProps}>
                {caption}
                {iconName && (
                    <IconContainer>
                        <Icon name={iconName} size="24px" leftIndent="8px" />
                    </IconContainer>
                )}
            </ReactLinkButton>
        );
    }
    const nativeType: NativeButtonType | undefined = type;
    const buttonProps = rest as ComponentPropsWithoutRef<typeof CommonButton>;
    return (
        <CommonButton ref={ref as Ref<HTMLButtonElement>} type={nativeType} data-testid={dataTestId} {...buttonProps}>
            {caption}
            {children}
            {iconName && (
                <IconContainer>
                    <Icon name={iconName} size="24px" leftIndent="8px" />
                </IconContainer>
            )}
        </CommonButton>
    );
});

Button.propTypes = {
    type: PropTypes.oneOf(['button', 'submit', 'link', 'reactLink']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'secondary_base', 'secondary_light', 'tertiary', 'error', 'danger', 'hidden', 'row']),
    size: PropTypes.oneOf(['tight', 'fit', 'small']),
    raised: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    disabled: PropTypes.bool,
    isInverted: PropTypes.bool,
    href: PropTypes.string,
    to: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    title: PropTypes.string,
    tabIndex: PropTypes.number,
    form: PropTypes.string, // для того, чтобы можно было связывать кнопку submit по id с form, если она находится вне form (н-р в попапах)
    caption: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    dataTestId: PropTypes.string, // для html аттрибута data-testid (babel plugin "react-remove-properties")
    negativeMargin: PropTypes.bool, // чтобы выравнивать row-кнопки по контенту
    onClick: PropTypes.func,
    textCenter: PropTypes.bool,
    iconName: PropTypes.string,
};
